import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Skeleton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import DataTable from "../../components/brand/DataTable";
import Panel from "../../components/brand/Panel";
import {
  createBankAccount,
  deleteBankAccount,
  getBankAccounts,
  importBankCsv,
  updateBankAccount,
} from "../../api/financeApi";
import { getApiErrorMessage } from "../../utils/concurrency";
import { formatDateUk } from "../../utils/dateDisplay";

const PAGE_SIZE = 10;

const emptyForm = {
  bank_name: "",
  account_name: "",
  sort_code: "",
  account_number_last4: "",
};

const formatSynced = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${formatDateUk(date.toISOString().slice(0, 10))} ${date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

export default function BankAccountsPanel({ propertyId, property, onMessage, onError }) {
  const [accounts, setAccounts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      onError?.("");
      const res = await getBankAccounts(propertyId, page, { page_size: PAGE_SIZE });
      setAccounts(res.data.results || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to load bank accounts."));
    } finally {
      setLoading(false);
    }
  }, [propertyId, page, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    const accountNumber = property?.account_number || "";
    setEditing(null);
    setForm({
      bank_name: property?.bank_name || "",
      account_name: "",
      sort_code: property?.sort_code || "",
      account_number_last4: accountNumber ? String(accountNumber).slice(-4) : "",
    });
    setDialogOpen(true);
  };

  const openEdit = (account) => {
    setEditing(account);
    setForm({
      bank_name: account.bank_name || "",
      account_name: account.account_name || "",
      sort_code: account.sort_code || "",
      account_number_last4: account.account_number_last4 || "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!(form.bank_name || "").trim()) {
      onError?.("Bank name is required.");
      return;
    }
    const payload = {
      bank_name: form.bank_name.trim(),
      account_name: form.account_name.trim() || "",
      sort_code: form.sort_code.trim() || "",
      account_number_last4: form.account_number_last4.trim() || "",
      currency: "GBP",
    };
    try {
      setSaving(true);
      onError?.("");
      if (editing) {
        await updateBankAccount(propertyId, editing.id, {
          ...payload,
          row_version: editing.row_version,
        });
        setDialogOpen(false);
        setEditing(null);
        onMessage?.("Bank account updated.");
        await load();
      } else {
        await createBankAccount(propertyId, {
          bank_name: payload.bank_name,
          account_name: payload.account_name || undefined,
          sort_code: payload.sort_code || undefined,
          account_number_last4: payload.account_number_last4 || undefined,
          currency: "GBP",
        });
        setDialogOpen(false);
        onMessage?.("Bank account created.");
        if (page !== 1) setPage(1);
        else await load();
      }
    } catch (e) {
      onError?.(
        getApiErrorMessage(
          e,
          editing ? "Failed to update bank account." : "Failed to create bank account."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!accountToDelete) return;
    try {
      setDeleting(true);
      onError?.("");
      await deleteBankAccount(propertyId, accountToDelete.id);
      setAccountToDelete(null);
      onMessage?.("Bank account removed.");
      if (accounts.length === 1 && page > 1) setPage((prev) => prev - 1);
      else await load();
    } catch (e) {
      setAccountToDelete(null);
      onError?.(getApiErrorMessage(e, "Failed to remove bank account."));
    } finally {
      setDeleting(false);
    }
  };

  const handleImportClick = (accountId) => {
    fileInputs.current[accountId]?.click();
  };

  const handleImportFile = async (accountId, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      setImportingId(accountId);
      onError?.("");
      const res = await importBankCsv(propertyId, accountId, file);
      const created = res.data.created_count ?? 0;
      const skipped = res.data.skipped_count ?? 0;
      onMessage?.(
        `Imported ${created} statement line${created === 1 ? "" : "s"}` +
          (skipped ? ` (${skipped} duplicate${skipped === 1 ? "" : "s"} skipped)` : "") +
          "."
      );
      await load();
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to import bank CSV."));
    } finally {
      setImportingId(null);
    }
  };

  return (
    <>
      <Panel
        title="Bank accounts"
        noBodyPadding={false}
        right={
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add bank account
          </Button>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manual CSV import until live bank feeds are connected. Expected columns: Date, Amount (or
          Paid In / Paid Out), optional Description, Reference and Counterparty. A demo file for Oak
          Court is at docs/samples/oak-court-bank-sample.csv.
        </Typography>

        {loading ? (
          <Skeleton height={180} />
        ) : accounts.length === 0 ? (
          <Alert severity="info">
            No bank accounts yet. Add one to import statement CSVs for reconciliation.
          </Alert>
        ) : (
          <TableContainer>
            <DataTable size="small" aria-label="Bank accounts">
              <TableHead>
                <TableRow>
                  <TableCell>Bank</TableCell>
                  <TableCell>Account name</TableCell>
                  <TableCell>Sort code</TableCell>
                  <TableCell>Last 4</TableCell>
                  <TableCell>Feed</TableCell>
                  <TableCell align="right">Unreconciled</TableCell>
                  <TableCell>Last synced</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>{account.bank_name}</TableCell>
                    <TableCell>{account.account_name || "—"}</TableCell>
                    <TableCell>{account.sort_code || "—"}</TableCell>
                    <TableCell>{account.account_number_last4 || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={account.feed_status || "manual"} />
                    </TableCell>
                    <TableCell align="right">{account.unreconciled_count ?? 0}</TableCell>
                    <TableCell>{formatSynced(account.last_synced_at_utc)}</TableCell>
                    <TableCell align="right">
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        hidden
                        ref={(el) => {
                          fileInputs.current[account.id] = el;
                        }}
                        onChange={(e) => handleImportFile(account.id, e)}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            importingId === account.id ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <UploadFileIcon />
                            )
                          }
                          disabled={importingId === account.id}
                          onClick={() => handleImportClick(account.id)}
                        >
                          Import CSV
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => openEdit(account)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => setAccountToDelete(account)}
                        >
                          Remove
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </TableContainer>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} />
          </Box>
        )}
      </Panel>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit bank account" : "Add bank account"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Bank name"
            size="small"
            required
            value={form.bank_name}
            onChange={(e) => setForm((prev) => ({ ...prev, bank_name: e.target.value }))}
          />
          <TextField
            label="Account name"
            size="small"
            value={form.account_name}
            onChange={(e) => setForm((prev) => ({ ...prev, account_name: e.target.value }))}
          />
          <TextField
            label="Sort code"
            size="small"
            value={form.sort_code}
            onChange={(e) => setForm((prev) => ({ ...prev, sort_code: e.target.value }))}
          />
          <TextField
            label="Account number (last 4)"
            size="small"
            value={form.account_number_last4}
            inputProps={{ maxLength: 4 }}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                account_number_last4: e.target.value.replace(/\D/g, "").slice(-4),
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : editing ? (
                <SaveIcon />
              ) : (
                <AddIcon />
              )
            }
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : editing ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(accountToDelete)}
        onClose={() => !deleting && setAccountToDelete(null)}
      >
        <DialogTitle>Remove this bank account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Remove "${accountToDelete?.bank_name || "this account"}${
              accountToDelete?.account_name ? ` — ${accountToDelete.account_name}` : ""
            }"? Unreconciled statement lines will be deleted. Accounts with reconciled lines cannot be removed.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<CloseIcon />}
            onClick={() => setAccountToDelete(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={
              deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
            }
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
